import { analytics } from '@moonpig/launchpad-analytics'
import {
  navigatePagesEvent,
  zeroResultsEvent,
  searchEvent,
  collectionResultsEvent,
} from '../../pages/SearchPage/events'
import {
  createGenericImpressionEvent,
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { PageTypeEnum } from '@moonpig/web-shared-products'
import {
  SearchStoreState,
  TrackingActionTypes,
  TrackingActionsEnum,
  TrackingPayload,
} from '../types'
import { getTrackingLabelForSortOrder } from '../../utils/getTrackingLabel'
import { createSearchGAEvent } from '../../analytics/createSearchGAEvent'
import { createViewGalleryResultsGAEvent } from '../../analytics/createViewGalleryResultsGAEvent'
import { QueryProductsProps } from '../slices/products'
import { trackLoadedProductsEvent } from './events'
import { generateDepartmentHeadersClickGAEvent } from '../../analytics/GAEvents'
import { ClearFiltersProps } from '../slices/filters'
import {
  getClearFiltersEventLabel,
  getNoResultsClearFiltersEventLabel,
} from '../helpers'
import { ApplyIntegerFilterProps } from '../slices/filters/types'
import { ApplicableFilter, IntegerFilter } from '../../services/types/services'
import { PageNavigationSource } from '../slices/products/types'

export const trackEvent = ({
  action,
  oldValues,
  newValues,
  payload,
}: {
  action: TrackingActionTypes
  payload: TrackingPayload
  oldValues: SearchStoreState
  newValues: SearchStoreState
}) => {
  const isLaunchpadAnalytics = newValues.launchpadAnalytics === true
  const category = `${newValues.pageContext.type} action`.toLowerCase()

  switch (action) {
    case TrackingActionsEnum.toggleFiltersMenu:
      // Launchpad event: apps/explore/find/src/components/FiltersMenu/components/Toggle/Toggle.tsx and components/FiltersMenu/components/Header/Header.tsx
      if (isLaunchpadAnalytics) break
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: newValues.filtersMenuOpen
            ? 'filters opened'
            : 'filters closed',
          eventLabel: newValues.pageContext.title,
        }),
      )
      break

    case TrackingActionsEnum.toggleFiltersCategory:
      // Launchpad event: apps/explore/find/src/components/FiltersMenu/components/TopLevelFilterButton/TopLevelFilterButton.tsx
      if (isLaunchpadAnalytics) break
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction:
            newValues.selectedCategories.length >
            oldValues.selectedCategories.length
              ? 'expand'
              : 'collapse',
          eventLabel:
            newValues.selectedCategories.length >
            oldValues.selectedCategories.length
              ? newValues.selectedCategories[
                  newValues.selectedCategories.length - 1
                ].label
              : oldValues.selectedCategories[
                  oldValues.selectedCategories.length - 1
                ]?.label || /* istanbul ignore next */ '',
        }),
      )
      break

    case TrackingActionsEnum.clearFilters:
      // Launchpad event: apps/explore/find/src/components/ClearButton/ClearButton.tsx
      if (isLaunchpadAnalytics) break
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'clear filters',
          eventLabel: getClearFiltersEventLabel(payload as ClearFiltersProps[]),
        }),
      )

      break

    case TrackingActionsEnum.sortBy:
      // Launchpad event: apps/explore/find/src/components/FiltersMenu/components/SortBy/SortOptions.tsx
      if (isLaunchpadAnalytics) break
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'sort by',
          eventLabel: getTrackingLabelForSortOrder(payload[0] as string),
        }),
      )
      break

    case TrackingActionsEnum.changeDepartment: {
      // Launchpad event: apps/explore/find/src/components/DepartmentTabs/DepartmentTabs.tsx
      if (isLaunchpadAnalytics) break
      const searchTerm =
        newValues.searchContext.searchTerm ||
        newValues.searchContext.selectedSuggestion?.label ||
        ''
      const departmentName = payload[0] as DepartmentsEnum
      const departmentTitle = getDepartmentTitle(departmentName)
      const departmentCount =
        newValues.searchContext.departmentCounts.find(
          d => d.department === departmentName,
        )?.count || /* istanbul ignore next */ 0
      trackGAEvent(
        generateDepartmentHeadersClickGAEvent({
          searchTerm,
          departmentTitle,
          departmentCount,
          pageType: newValues.pageContext.type,
          pageTitle: newValues.pageContext.title,
        }),
      )

      break
    }

    case TrackingActionsEnum.queryProducts:
      if (!(payload[0] as QueryProductsProps).isLoadMore) {
        const queryProductsProps = payload[0] as QueryProductsProps
        const selectedFilters =
          queryProductsProps.filters ||
          /* istanbul ignore next */ newValues.selectedFilters
        const selectedIntegerFilter = queryProductsProps.integerFilter
          ? queryProductsProps.filteringEvent?.filter
          : undefined

        if (newValues.pageContext.type === PageTypeEnum.SEARCH) {
          const search = createSearchGAEvent({
            correctedSearchTerm:
              newValues.searchContext.correctedSearchTerm || '',
            department: newValues.searchContext.departments,
            filters: [
              ...selectedFilters,
              ...(selectedIntegerFilter
                ? [selectedIntegerFilter as IntegerFilter]
                : []),
            ].map(f => ({
              key: f.id,
              group: f.parent,
            })),
            filteringEvent: newValues.filteringEvent,
            numberOfResults: newValues.results.totalCount,
            searchTerm: newValues.searchContext.searchTerm,
            sort: newValues.sortValue,
            selectedSuggestion: newValues.searchContext.selectedSuggestion,
            isTrendingSuggestion:
              !!newValues.searchContext.isTrendingSuggestion,
            showNbas: newValues.hasNbaFilters,
            showRudeProducts: newValues.hasRudeProducts,
            redirectedFrom: newValues.searchContext.redirectedFrom,
            redirectionSource: newValues.searchContext.redirectionSource,
          })
          // Legacy event: apps/explore/find/src/analytics/createViewResultsEvent.ts
          if (isLaunchpadAnalytics) {
            analytics.send(
              searchEvent()
                .withEventData(search.event_data)
                .withResultsData(search.results_data)
                .withFilterData(search.filter_data),
            )
          } else {
            // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
            trackGAEvent(search)
          }

          trackLoadedProductsEvent({
            results: newValues.results,
            searchContext: newValues.searchContext,
            preAppliedFilters: newValues.preAppliedFilters,
            selectedFilters: newValues.selectedFilters,
            pageContext: newValues.pageContext,
            lastLoadedPage: newValues.lastLoadedPage,
            filteringEvent: newValues.filteringEvent,
            isPrevious: payload[0] as boolean,
            galleryPageSize: newValues.galleryPageSize,
            launchpadAnalytics: isLaunchpadAnalytics,
          })
        }

        if (
          newValues.pageContext.type === PageTypeEnum.GALLERY ||
          newValues.pageContext.type === PageTypeEnum.COLLECTION
        ) {
          const galleryResults = createViewGalleryResultsGAEvent({
            department: newValues.searchContext.departments,
            filters: [
              ...newValues.preAppliedFilters,
              ...[
                ...selectedFilters,
                ...(selectedIntegerFilter
                  ? [selectedIntegerFilter as IntegerFilter]
                  : []),
              ].map(f => ({
                key: f.id,
                group: f.parent,
              })),
            ],
            filteringEvent: newValues.filteringEvent,
            numberOfResults: newValues.results.totalCount,
            pageTitle: newValues.pageContext.title,
            sort: newValues.sortValue,
            showNbas: newValues.hasNbaFilters,
            showRudeProducts: newValues.hasRudeProducts,
            pageType: newValues.pageContext.type as PageTypeEnum,
          })
          // Legacy event: apps/explore/find/src/analytics/createViewResultsEvent.ts
          if (isLaunchpadAnalytics) {
            analytics.send(
              collectionResultsEvent()
                .withEventData(galleryResults.event_data)
                .withResultsData(galleryResults.results_data)
                .withFilterData(galleryResults.filter_data),
            )
          } else {
            // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
            trackGAEvent(galleryResults)
          }

          trackLoadedProductsEvent({
            results: newValues.results,
            searchContext: newValues.searchContext,
            preAppliedFilters: newValues.preAppliedFilters,
            selectedFilters: newValues.selectedFilters,
            pageContext: newValues.pageContext,
            lastLoadedPage: newValues.lastLoadedPage,
            filteringEvent: newValues.filteringEvent,
            isPrevious: payload[0] as boolean,
            galleryPageSize: newValues.galleryPageSize,
            launchpadAnalytics: isLaunchpadAnalytics,
          })
        }
      }
      break

    case TrackingActionsEnum.loadMoreProducts:
      trackLoadedProductsEvent({
        results: newValues.results,
        searchContext: newValues.searchContext,
        preAppliedFilters: newValues.preAppliedFilters,
        selectedFilters: newValues.selectedFilters,
        pageContext: newValues.pageContext,
        lastLoadedPage: newValues.lastLoadedPage,
        filteringEvent: newValues.filteringEvent,
        isPrevious: payload[0] as boolean,
        galleryPageSize: newValues.galleryPageSize,
        launchpadAnalytics: isLaunchpadAnalytics,
      })
      break

    case TrackingActionsEnum.loadPage:
      if (isLaunchpadAnalytics) {
        // Click events are owned by the Launchpad pagination buttons.
        if (payload[1] === PageNavigationSource.History) {
          analytics.send(
            navigatePagesEvent()
              .withEventData({
                category,
                label: `${payload[1]} page`.toLowerCase(),
              })
              .withContentData({
                content_type:
                  `${category} | navigate pages | ${payload[1]} page`.toLowerCase(),
              }),
          )
        }
        break
      }
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'navigate pages',
          eventLabel:
            payload[1] !== PageNavigationSource.Number
              ? `${payload[1]} page`
              : `go to page ${payload[0]}`,
        }),
      )
      break

    case TrackingActionsEnum.applyIntegerFilter:
      if (isLaunchpadAnalytics) {
        if (newValues.noResultsFilters.length > 0) {
          const count = newValues.noResultsFilters.length.toString()
          analytics.send(
            zeroResultsEvent().withEventData({
              category: `${newValues.pageContext.type} results`.toLowerCase(),
              label: count,
            }),
          )
        }
        break
      }
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'show milestone results',
          eventLabel:
            (
              payload[0] as ApplyIntegerFilterProps
            )?.filter?.selectedValue?.toString() ||
            (payload[0] as ApplyIntegerFilterProps)?.value?.toString() ||
            /* istanbul ignore next */ '',
        }),
      )
      if (newValues.noResultsFilters.length > 0) {
        trackGAEvent(
          createGenericImpressionEvent({
            category: `${newValues.pageContext.type} results`,
            action: 'zero results viewed',
            label: newValues.noResultsFilters.length.toString(),
          }),
        )
      }
      break

    case TrackingActionsEnum.clearFiltersWhenNoResults:
      // Launchpad event: apps/explore/find/src/components/FilterChip/FilterChip.tsx
      if (isLaunchpadAnalytics) break
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: `${newValues.pageContext.type} action`,
          eventAction: 'clear filters',
          eventLabel: getNoResultsClearFiltersEventLabel(
            payload as { filter: ApplicableFilter }[],
          ),
        }),
      )

      break
  }
}
